import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  importProvidersFrom,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { TitleStrategy, provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { FormsModule } from '@angular/forms';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { LayoutModule } from './layout/layout.module';
import { SharedModule } from './shared/shared.module';
import { appRoutes } from './app.routes';
import {
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';
import {
  apiBearerCondition,
  keycloakConfig,
  provideKeycloakInit,
} from './core/config/keycloak.config';
import { httpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { ecommerceBearerInterceptor } from './core/interceptors/ecommerce-bearer.interceptor';
import { ecommerceApiErrorInterceptor } from './core/interceptors/ecommerce-api-error.interceptor';
import { retryInterceptor } from './core/interceptors/retry.interceptor';
import { PageTitleStrategy } from './core/services/page-title.strategy';

// Preset PrimeNG allineato ai token Novatellus (design handoff, Parte C).
// In PrimeNG 20 e' questo preset a generare le variabili --p-* consumate dai
// componenti: i valori vanno tenuti in pari con theme.css, altrimenti i
// componenti restano sulla palette precedente sotto gli override CSS.
const NfarmPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '6px',
      sm: '9px',
      md: '11px',
      lg: '12px',
      xl: '16px',
    },
  },
  semantic: {
    primary: {
      50: '#F3F8F5',
      100: '#EAF1ED',
      200: '#E6EFE9',
      300: '#CBDDD3',
      400: '#A8C6B6',
      500: '#46806A',
      600: '#46806A',
      700: '#3B6E5A',
      800: '#2E5747',
      900: '#22352E',
      950: '#16241E',
    },
    formField: {
      borderRadius: '10px',
    },
    content: {
      borderRadius: '16px',
    },
    focusRing: {
      // Il verde tenue precedente era invisibile sui fondi chiari (1,1:1).
      // Il verde scuro raggiunge 5,9:1 su card e pagina (WCAG 2.4.7, 1.4.11).
      width: '2px',
      style: 'solid',
      color: '{primary.800}',
      offset: '2px',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#FBFAF8',
          100: '#F1EEE8',
          200: '#EDE9E2',
          300: '#E3DFD7',
          400: '#C6C2B9',
          500: '#A8A49A',
          600: '#8A9089',
          700: '#6D746E',
          800: '#46504A',
          900: '#22352E',
          950: '#16241E',
        },
        primary: {
          // Allineato a --primary-color del tema: il bianco sopra raggiunge
          // 4,61:1, soglia WCAG 1.4.3 per il testo dei pulsanti pieni.
          color: '#46806A',
          contrastColor: '#ffffff',
          hoverColor: '#3B6E5A',
          activeColor: '#2E5747',
        },
        highlight: {
          background: '#F3F8F5',
          focusBackground: '#EAF1ED',
          // Testo su fondo chiaro: serve la variante più scura.
          color: '#437965',
          focusColor: '#3B6E5A',
        },
        text: {
          color: '#22352E',
          hoverColor: '#22352E',
          mutedColor: '#7B837C',
          hoverMutedColor: '#46504A',
        },
        content: {
          background: '#FBFAF8',
          hoverBackground: '#EAE7E0',
          borderColor: '#EDE9E2',
          color: '#22352E',
          hoverColor: '#22352E',
        },
        formField: {
          background: '#ffffff',
          borderColor: '#E3DFD7',
          hoverBorderColor: '#A8C6B6',
          focusBorderColor: '#46806A',
          invalidBorderColor: '#C98F87',
          color: '#22352E',
          placeholderColor: '#B4B0A6',
          invalidPlaceholderColor: '#C98F87',
        },
        mask: {
          background: 'rgba(20, 32, 27, 0.52)',
          color: '#ffffff',
        },
        overlay: {
          select: {
            background: '#ffffff',
            borderColor: '#EDE9E2',
            color: '#22352E',
          },
          popover: {
            background: '#ffffff',
            borderColor: '#EDE9E2',
            color: '#22352E',
          },
          modal: {
            background: '#FBFAF8',
            borderColor: '#EDE9E2',
            color: '#22352E',
          },
        },
      },
    },
  },
});

// Formattazione italiana per date, numeri e valuta (es. "270,00 €"); EUR come
// valuta di default quando un pipe currency non riceve il codice dal backend.
registerLocaleData(localeIt);

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'it' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    // Senza `initOptions`: l'init lo esegue `provideKeycloakInit`, che
    // registra l'esito per interrompere il loop di login (vedi keycloak.config.ts).
    provideKeycloak({ config: keycloakConfig }),
    provideKeycloakInit(),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [apiBearerCondition],
    },
    provideHttpClient(
      withInterceptors([
        includeBearerTokenInterceptor,
        ecommerceBearerInterceptor,
        // Dopo il bearer: traduce gli errori applicativi che il contratto
        // ecommerce consegna con HTTP 200, così risalgono come eccezioni.
        ecommerceApiErrorInterceptor,
        httpErrorInterceptor,
        retryInterceptor,
      ])
    ),
    provideRouter(appRoutes),
    // Titolo del documento per pagina, ricavato dai breadcrumb delle rotte.
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    provideAnimationsAsync(),
    providePrimeNG({ theme: { preset: NfarmPreset } }),
    importProvidersFrom(FormsModule, LayoutModule, SharedModule, ToastModule),
    MessageService,
  ],
};
